import type { ProjectListItem, Stage } from '../../api/types'
import { parseDateOnly } from '../../lib/format'

/**
 * The 30-Day Focus ranking (from the screen guide):
 *   (1) overdue or soonest next follow-up, (2) stage weight – Tender, then Opportunity, then Lead,
 *   (3) ₹ value as a tie-breaker. Won, Lost and archived projects are left out.
 *
 * How (1) and (2) combine: overdue projects always come first. Within each group, a project
 * scores "urgency points" = days overdue (negative when the date is still ahead)
 * + 9 points per stage level. So a Tender due in 9 days ranks level with an Opportunity due today.
 * This weighting reproduces the order shown in the guide exactly.
 */
const POINTS_PER_STAGE_LEVEL = 9
const STAGE_LEVEL: Partial<Record<Stage, number>> = { Lead: 1, Opportunity: 2, Tender: 3 }

/** Projects with no follow-up date sort as if due far in the future. */
const NO_DATE_DAYS_AHEAD = 365

export const FOCUS_LIST_SIZE = 12

export function isActive(p: ProjectListItem): boolean {
  return !p.isArchived && STAGE_LEVEL[p.stage] !== undefined
}

/** Days from today until the follow-up (negative = overdue). */
export function daysUntilFollowUp(p: ProjectListItem, today: Date): number {
  if (!p.nextFollowUp) return NO_DATE_DAYS_AHEAD
  const due = parseDateOnly(p.nextFollowUp)
  return Math.round((due.getTime() - today.getTime()) / 86_400_000)
}

export function urgencyPoints(p: ProjectListItem, today: Date): number {
  return -daysUntilFollowUp(p, today) + POINTS_PER_STAGE_LEVEL * (STAGE_LEVEL[p.stage] ?? 0)
}

/** Every active project, most urgent first. */
export function rankActive(projects: ProjectListItem[], today: Date): ProjectListItem[] {
  return projects
    .filter(isActive)
    .sort(
      (a, b) =>
        Number(b.isOverdue) - Number(a.isOverdue) ||
        urgencyPoints(b, today) - urgencyPoints(a, today) ||
        b.valueRupees - a.valueRupees,
    )
}

/** The ranked shortlist: the top 12 active projects. */
export function rankFocus(projects: ProjectListItem[], today: Date): ProjectListItem[] {
  return rankActive(projects, today).slice(0, FOCUS_LIST_SIZE)
}

/** Starred projects in ranking order; starred Won/Lost projects go last. */
export function starredInRankOrder(projects: ProjectListItem[], today: Date): ProjectListItem[] {
  const order = rankActive(projects, today)
  const position = (p: ProjectListItem) => {
    const i = order.indexOf(p)
    return i === -1 ? Number.MAX_SAFE_INTEGER : i
  }
  return projects.filter((p) => p.isStarred && !p.isArchived).sort((a, b) => position(a) - position(b))
}

/** Something missing from an active project, and what to do about it. */
export type Gap = { label: string; action: string }

/** "Needs attention": what each active project is missing. */
export function findGaps(p: ProjectListItem): Gap[] {
  const gaps: Gap[] = []
  if (!p.nextFollowUp) gaps.push({ label: 'Missing follow-up date', action: 'set the next follow-up' })
  if (p.stakeholderNames.length === 0) gaps.push({ label: 'Missing stakeholder', action: 'link a stakeholder' })
  if (!p.triggerSource) gaps.push({ label: 'Missing trigger source', action: 'record what started it' })
  if (p.fundingStatus === 'NotSure') gaps.push({ label: 'Missing funding clarity', action: 'fill in funding clarity' })
  return gaps
}

/** Active projects with at least one gap, most gaps first, then most valuable. */
export function needsAttention(projects: ProjectListItem[]): { project: ProjectListItem; gaps: Gap[] }[] {
  return projects
    .filter(isActive)
    .map((project) => ({ project, gaps: findGaps(project) }))
    .filter((x) => x.gaps.length > 0)
    .sort((a, b) => b.gaps.length - a.gaps.length || b.project.valueRupees - a.project.valueRupees)
}

/** Midnight today, local time – the same "day" the follow-up dates use. */
export function startOfToday(): Date {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}
